import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./css/CityParametersManagementPage.module.css";
import { Tabs, Tab, Box } from "@mui/material";
import { InputField } from "@/components/inputFields/inputFields";
import { Button } from "@/components/Buttons";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { getCityAttributes, updateCityAttributes, getSlot, updateSlot, previewUpdateSlotImpact } from "@/api/cityAttributes";
import { getCity } from "@/api/city";
import { CityAttributes } from "@/types";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`parameters-tabpanel-${index}`}
      aria-labelledby={`parameters-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const CityParametersManagementPage: React.FC = () => {
  const { cityId } = useParams<{ cityId: string }>();
  const user = useSelector((state: RootState) => state.user.user);
  const { t } = useTranslation('CityParametersManagementPage');
  
  // Slot management state
  const [currentSlot, setCurrentSlot] = useState(1); // Default to slot 1 (נתוני עיר)
  const [affectedAlternativesCount, setAffectedAlternativesCount] = useState<number>(0);
  
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cityName, setCityName] = useState("");
  
  // Slot names
  const SLOT_NAMES: Record<number, string> = {
    1: t('slotName1'),
    2: t('slotName2'),
    3: t('slotName3')
  };
  
  // General parameters
  const [sourceYear, setSourceYear] = useState(2025); // Read-only, loaded from city data
  const [targetYear, setTargetYear] = useState<number | undefined>(undefined);
  const [currentPopulation, setCurrentPopulation] = useState<number | undefined>(undefined);
  const [futurePopulation, setFuturePopulation] = useState<number | undefined>(undefined);
  const [populationGrowthRate, setPopulationGrowthRate] = useState<number | undefined>(undefined);
  const [residentsCount, setResidentsCount] = useState<number | undefined>(undefined);
  const [households, setHouseholds] = useState<number | undefined>(undefined);
  
  // Area parameters - Current
  const [residentialArea, setResidentialArea] = useState<number | undefined>(undefined);
  const [commercialArea, setCommercialArea] = useState<number | undefined>(undefined);
  const [industrialArea, setIndustrialArea] = useState<number | undefined>(undefined);
  const [publicArea, setPublicArea] = useState<number | undefined>(undefined);
  const [culturalArea, setCulturalArea] = useState<number | undefined>(undefined);
  const [transportArea, setTransportArea] = useState<number | undefined>(undefined);
  const [parkingInfraArea, setParkingInfraArea] = useState<number | undefined>(undefined);
  
  // Area parameters - Planned
  const [residentialAreaPlanned, setResidentialAreaPlanned] = useState<number | undefined>(undefined);
  const [commercialAreaPlanned, setCommercialAreaPlanned] = useState<number | undefined>(undefined);
  const [industrialAreaPlanned, setIndustrialAreaPlanned] = useState<number | undefined>(undefined);
  const [publicAreaPlanned, setPublicAreaPlanned] = useState<number | undefined>(undefined);
  const [culturalAreaPlanned, setCulturalAreaPlanned] = useState<number | undefined>(undefined);
  const [transportAreaPlanned, setTransportAreaPlanned] = useState<number | undefined>(undefined);
  const [parkingInfraAreaPlanned, setParkingInfraAreaPlanned] = useState<number | undefined>(undefined);
  
  // Vehicle parameters
  const [vehiclesCount, setVehiclesCount] = useState<number | undefined>(undefined);
  const [vehiclesCountFuture, setVehiclesCountFuture] = useState<number | undefined>(undefined);
  const [carAvgAge, setCarAvgAge] = useState<number | undefined>(undefined);
  const [residentialUnitsCount, setResidentialUnitsCount] = useState<number | undefined>(undefined);
  const [residentialUnitsCountFuture, setResidentialUnitsCountFuture] = useState<number | undefined>(undefined);
  
  // Energy parameters - Consumption
  const [residentialEnergyConsumption, setResidentialEnergyConsumption] = useState<number | undefined>(undefined);
  const [commercialEnergyConsumption, setCommercialEnergyConsumption] = useState<number | undefined>(undefined);
  const [industrialEnergyConsumption, setIndustrialEnergyConsumption] = useState<number | undefined>(undefined);
  const [publicEnergyConsumption, setPublicEnergyConsumption] = useState<number | undefined>(undefined);
  
  // Energy parameters - Renewable
  const [installedRenewableCapacity, setInstalledRenewableCapacity] = useState<number | undefined>(undefined);
  const [renewablePotential, setRenewablePotential] = useState<number | undefined>(undefined);
  const [plannedRenewableInfo, setPlannedRenewableInfo] = useState<number | undefined>(undefined);
  
  // Energy infrastructure
  const [substationCountCurrent, setSubstationCountCurrent] = useState<number | undefined>(undefined);
  const [substationCountFuture, setSubstationCountFuture] = useState<number | undefined>(undefined);
  const [futurePowerAdditionIec, setFuturePowerAdditionIec] = useState<number | undefined>(undefined);
  
  // Parking standards
  const [parkingResidential, setParkingResidential] = useState<number | undefined>(undefined);
  const [parkingCommercial, setParkingCommercial] = useState<number | undefined>(undefined);
  const [parkingIndustrial, setParkingIndustrial] = useState<number | undefined>(undefined);
  const [parkingPublic, setParkingPublic] = useState<number | undefined>(undefined);
  
  // Charging standards
  const [chargingResidential, setChargingResidential] = useState<number | undefined>(undefined);
  const [chargingCommercial, setChargingCommercial] = useState<number | undefined>(undefined);
  const [chargingIndustrial, setChargingIndustrial] = useState<number | undefined>(undefined);
  const [chargingPublic, setChargingPublic] = useState<number | undefined>(undefined);
  const [localPublicChargingRatio, setLocalPublicChargingRatio] = useState<number | undefined>(undefined);
  
  // Tooltip definitions
  const tooltips = {
    // General parameters
    targetYear: "[שדה בסיס] שנת יעד לתכנון - השנה בה יושלם הפיתוח המתוכנן של העיר",
    currentPopulation: "[שדה בסיס] אוכלוסייה נוכחית - מספר התושבים בשנת המקור",
    futurePopulation: "[שדה בסיס] אוכלוסייה עתידית - מספר התושבים המתוכנן לשנת היעד",
    populationGrowthRate: "[שדה בסיס] קצב גידול אוכלוסייה שנתי - אחוז גידול ממוצע בשנה",
    residentsCount: "[שדה בסיס] מספר תושבים - כמות התושבים הנוכחית בעיר",
    households: "[שדה בסיס] מספר משקי בית - כמות משקי הבית בעיר",
    
    // Areas - Current
    residentialArea: "[שדה בסיס] שטח מגורים נוכחי (מ\"ר) - סך כל שטחי הבניה למגורים",
    commercialArea: "[שדה בסיס] שטח מסחרי נוכחי (מ\"ר) - סך כל שטחי הבניה המסחרית",
    industrialArea: "[שדה בסיס] שטח תעשייתי נוכחי (מ\"ר) - סך כל שטחי הבניה התעשייתית",
    publicArea: "[שדה בסיס] שטח ציבורי נוכחי (מ\"ר) - סך כל שטחי מבני ציבור",
    culturalArea: "[שדה בסיס] שטח תרבות נוכחי (מ\"ר) - סך כל שטחי מבני תרבות",
    transportArea: "[שדה בסיס] שטח תחבורה נוכחי (מ\"ר) - שטחי תחנות ותשתיות תחבורה",
    parkingInfraArea: "[שדה בסיס] שטח תשתית חניה נוכחי (מ\"ר) - שטחי חניונים ומתקני חניה",
    
    // Areas - Planned
    residentialAreaPlanned: "[שדה בסיס] שטח מגורים מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי מגורים",
    commercialAreaPlanned: "[שדה בסיס] שטח מסחרי מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי מסחר",
    industrialAreaPlanned: "[שדה בסיס] שטח תעשייתי מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי תעשייה",
    publicAreaPlanned: "[שדה בסיס] שטח ציבורי מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי ציבור",
    culturalAreaPlanned: "[שדה בסיס] שטח תרבות מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי תרבות",
    transportAreaPlanned: "[שדה בסיס] שטח תחבורה מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי תחבורה",
    parkingInfraAreaPlanned: "[שדה בסיס] שטח תשתית חניה מתוכנן (מ\"ר) - הוספה מתוכננת לשטחי חניה",
    
    // Vehicles
    vehiclesCount: "[שדה בסיס] כמות רכבים נוכחית - מספר כלי הרכב הרשומים בעיר כיום",
    vehiclesCountFuture: "[שדה בסיס] כמות רכבים עתידית - תחזית כמות כלי רכב לשנת היעד",
    carAvgAge: "[שדה בסיס] גיל ממוצע רכב - גיל ממוצע של צי הרכבים בעיר",
    residentialUnitsCount: "[שדה בסיס] יחידות דיור נוכחיות - מספר דירות המגורים הקיימות",
    residentialUnitsCountFuture: "[שדה בסיס] יחידות דיור עתידיות - תחזית מספר דירות לשנת היעד",
    motorizationLevel: "[שדה מחושב] רמת מינוע - מחושב אוטומטית: (כמות רכבים ÷ מספר תושבים) × 1000. ניתן לעדכן ידנית במידת הצורך",
    publicBuildingsPercentage: "[שדה מחושב] אחוז מבני ציבור - מחושב אוטומטית: ((שטח כולל - שטח מגורים) ÷ שטח כולל) × 100. ניתן לעדכן ידנית במידת הצורך",
    residentialBuildingDensity: "[שדה מחושב] צפיפות דיור - מחושבת אוטומטית: אוכלוסייה ÷ יחידות דיור. ניתן לעדכן ידנית במידת הצורך",
    avgSqmPerResidentialUnit: "[שדה מחושב] ממוצע מ\"ר ליחידת דיור - מחושב אוטומטית: שטח מגורים ÷ יחידות דיור. ניתן לעדכן ידנית במידת הצורך",
    
    // Energy - Consumption
    residentialEnergyConsumption: "[שדה בסיס] צריכת חשמל מגורים (KWh) - צריכה שנתית משקי בית",
    commercialEnergyConsumption: "[שדה בסיס] צריכת חשמל מסחרי (KWh) - צריכה שנתית מבני מסחר",
    industrialEnergyConsumption: "[שדה בסיס] צריכת חשמל תעשייתי (KWh) - צריכה שנתית מפעלים",
    publicEnergyConsumption: "[שדה בסיס] צריכת חשמל ציבורי (KWh) - צריכה שנתית מבני ציבור",
    
    // Energy - Renewable
    installedRenewableCapacity: "[שדה בסיס] קיבולת אנרגיה מתחדשת מותקנת (KW) - הספק מערכות סולאריות קיימות",
    renewablePotential: "[שדה בסיס] פוטנציאל אנרגיה מתחדשת (KW) - הספק מקסימלי אפשרי",
    plannedRenewableInfo: "[שדה בסיס] מידע על אנרגיה מתחדשת מתוכננת - תוכניות להתקנת מערכות חדשות",
    
    // Infrastructure
    substationCountCurrent: "[שדה בסיס] מספר תחנות משנה נוכחיות - תחנות חשמל פעילות",
    substationCountFuture: "[שדה בסיס] מספר תחנות משנה עתידיות - תחנות חשמל מתוכננות",
    futurePowerAdditionIec: "[שדה בסיס] תוספת הספק עתידית חח\"י (KW) - תוספת הספק מתוכננת מחברת החשמל",
    
    // Parking standards
    parkingResidential: "[שדה בסיס] תקן חניה מגורים - מספר חניות נדרש ליחידת דיור",
    parkingCommercial: "[שדה בסיס] תקן חניה מסחרי - מספר חניות נדרש ל-100 מ\"ר מסחר",
    parkingIndustrial: "[שדה בסיס] תקן חניה תעשייתי - מספר חניות נדרש ל-100 מ\"ר תעשייה",
    parkingPublic: "[שדה בסיס] תקן חניה ציבורי - מספר חניות נדרש ל-100 מ\"ר ציבור",
    
    // Charging standards
    chargingResidential: "[שדה בסיס] תקן טעינה מגורים (%) - אחוז חניות עם נקודת טעינה חשמלית",
    chargingCommercial: "[שדה בסיס] תקן טעינה מסחרי (%) - אחוז חניות עם נקודת טעינה",
    chargingIndustrial: "[שדה בסיס] תקן טעינה תעשייתי (%) - אחוז חניות עם נקודת טעינה",
    chargingPublic: "[שדה בסיס] תקן טעינה ציבורי (%) - אחוז חניות עם נקודת טעינה",
    localPublicChargingRatio: "[שדה בסיס] יחס טעינה ציבורית מקומית (%) - אחוז מהטעינה הכוללת בתחום העיר",
  };
  
  // Check admin access - allow both super_admin and admin (city admin)
  useEffect(() => {
    if (user.user_type !== "super_admin" && user.user_type !== "admin") {
      alert(t('alertNoPermission'));
      window.history.back();
      return;
    }
    
    // For city admin, ensure they can only access their own city
    if (user.user_type === "admin" && cityId && user.city_id !== cityId) {
      alert(t('alertNoPermissionOtherCity'));
      window.history.back();
    }
  }, [user.user_type, user.city_id, cityId]);

  // Fetch city data
  useEffect(() => {
    const fetchData = async () => {
      if (!cityId) return;
      
      try {
        // Fetch city basic info
        const cityData = await getCity({ city_id: cityId });
        console.log("City Data:", cityData);
        setCityName(cityData.name || "");
        
        // Fetch city attributes from current slot
        const attrs = await getSlot({ 
          city_id: cityId,
          slot_number: currentSlot
        });
        
        console.log(`Slot ${currentSlot} API Response:`, attrs);
        const data = attrs as any;
        
        // Check if slot is empty
        if (data?.is_empty) {
          console.log(`Slot ${currentSlot} is empty, clearing all fields`);
          // Clear all fields for empty slot
          clearAllFields();
          // Set sourceYear from city data as fallback for empty slots
          setSourceYear(cityData.source_year || new Date().getFullYear());
          return;
        }
        
        console.log("Extracted data:", data);
        
        // Set sourceYear from slot's current_year attribute
        setSourceYear(data?.current_year || cityData.source_year || new Date().getFullYear());
        
        // General
        setTargetYear(data?.target_year);
        setCurrentPopulation(data?.population_count);
        // Future population from dict
        const futurePopFromDict = data?.population_count_dict?.[data?.target_year]?.['כמות תושבים'];
        setFuturePopulation(data?.population_count_future || futurePopFromDict);
        setPopulationGrowthRate(data?.population_growth);
        setResidentsCount(data?.residents_count);
        setHouseholds(data?.households);
        
        // Areas - Current
        setResidentialArea(data?.residential_area);
        setCommercialArea(data?.commercial_area);
        setIndustrialArea(data?.industrial_area);
        setPublicArea(data?.public_area);
        setCulturalArea(data?.cultural_area);
        setTransportArea(data?.transport_area);
        setParkingInfraArea(data?.parking_infrastructure_area);
        
        // Areas - Planned (calculated from areas_dict future year vs current)
        const currentYear = data?.current_year || sourceYear;
        const targetYearValue = data?.target_year;
        const areasDict = data?.areas_dict || {};
        const currentAreas = areasDict[currentYear] || {};
        const futureAreas = areasDict[targetYearValue] || {};
        
        // Calculate planned as difference (future - current)
        if (futureAreas['מגורים'] && currentAreas['מגורים']) {
          setResidentialAreaPlanned(futureAreas['מגורים'] - currentAreas['מגורים']);
        }
        if (futureAreas['מסחר'] && currentAreas['מסחר']) {
          setCommercialAreaPlanned(futureAreas['מסחר'] - currentAreas['מסחר']);
        }
        if (futureAreas['תעשייה'] && currentAreas['תעשייה']) {
          setIndustrialAreaPlanned(futureAreas['תעשייה'] - currentAreas['תעשייה']);
        }
        if (futureAreas['ציבורי'] && currentAreas['ציבורי']) {
          setPublicAreaPlanned(futureAreas['ציבורי'] - currentAreas['ציבורי']);
        }
        
        // Vehicles
        setVehiclesCount(data?.vehicles_count);
        const futureVehicles = data?.vehicles_count_dict?.[targetYearValue]?.['כמות רכבים'];
        setVehiclesCountFuture(data?.vehicles_count_future || futureVehicles);
        setCarAvgAge(data?.car_avg_age);
        
        // Residential units from dict
        const unitsDict = data?.residential_units_count_dict || {};
        const currentYearUnits = unitsDict[currentYear]?.['כמות יח״ד'];
        const futureYearUnits = unitsDict[targetYearValue]?.['כמות יח״ד'];
        setResidentialUnitsCount(currentYearUnits);
        setResidentialUnitsCountFuture(futureYearUnits);
        
        // Energy - Consumption (from dict for current year)
        const energyDict = data?.energy_consumption_dict || {};
        const currentEnergy = energyDict[currentYear] || {};
        setResidentialEnergyConsumption(currentEnergy['מגורים']);
        setCommercialEnergyConsumption(currentEnergy['מסחר']);
        setIndustrialEnergyConsumption(currentEnergy['תעשייה']);
        setPublicEnergyConsumption(currentEnergy['ציבורי']);
        
        // Energy - Renewable
        setInstalledRenewableCapacity(data?.installed_renewable_energy_capacity);
        setRenewablePotential(data?.urban_renewable_energy_potential);
        setPlannedRenewableInfo(data?.planned_renewable_installations_info);
        
        // Energy - Infrastructure (from electricity dict)
        const electricityDict = data?.electricity || {};
        const currentElectricity = electricityDict[currentYear] || {};
        const futureElectricity = electricityDict[targetYearValue] || {};
        setSubstationCountCurrent(currentElectricity['מספר תחמשים']);
        setSubstationCountFuture(futureElectricity['מספר תחמשים']);
        setFuturePowerAdditionIec(futureElectricity['תוספת הספק חשמלי לפי חברת חשמל']);
        
        // Parking standards
        const parking = data?.urban_parking_standard || {};
        setParkingResidential(parking.urban_parking_standard_residential);
        setParkingCommercial(parking.urban_parking_standard_commercial);
        setParkingIndustrial(parking.urban_parking_standard_industrial);
        setParkingPublic(parking.urban_parking_standard_public);
        
        // Charging standards
        const charging = data?.charging_standard || {};
        setChargingResidential(charging.residential_charging_standard);
        setChargingCommercial(charging.commercial_charging_standard);
        setChargingIndustrial(charging.industrial_charging_standard);
        setChargingPublic(charging.public_charging_standard);
        setLocalPublicChargingRatio(charging.local_public_charging_users_ratio);
        
      } catch (error) {
        console.error("Failed to fetch city data:", error);
        alert(t('alertLoadError'));
      }
    };

    fetchData();
  }, [cityId, currentSlot]); // Re-fetch when slot changes

  // Clear all fields helper function
  const clearAllFields = () => {
    setTargetYear(undefined);
    setCurrentPopulation(undefined);
    setFuturePopulation(undefined);
    setPopulationGrowthRate(undefined);
    setResidentsCount(undefined);
    setHouseholds(undefined);
    setResidentialArea(undefined);
    setCommercialArea(undefined);
    setIndustrialArea(undefined);
    setPublicArea(undefined);
    setCulturalArea(undefined);
    setTransportArea(undefined);
    setParkingInfraArea(undefined);
    setResidentialAreaPlanned(undefined);
    setCommercialAreaPlanned(undefined);
    setIndustrialAreaPlanned(undefined);
    setPublicAreaPlanned(undefined);
    setCulturalAreaPlanned(undefined);
    setTransportAreaPlanned(undefined);
    setParkingInfraAreaPlanned(undefined);
    setVehiclesCount(undefined);
    setVehiclesCountFuture(undefined);
    setCarAvgAge(undefined);
    setResidentialUnitsCount(undefined);
    setResidentialUnitsCountFuture(undefined);
    setResidentialEnergyConsumption(undefined);
    setCommercialEnergyConsumption(undefined);
    setIndustrialEnergyConsumption(undefined);
    setPublicEnergyConsumption(undefined);
    setInstalledRenewableCapacity(undefined);
    setRenewablePotential(undefined);
    setPlannedRenewableInfo(undefined);
    setSubstationCountCurrent(undefined);
    setSubstationCountFuture(undefined);
    setFuturePowerAdditionIec(undefined);
    setParkingResidential(undefined);
    setParkingCommercial(undefined);
    setParkingIndustrial(undefined);
    setParkingPublic(undefined);
    setChargingResidential(undefined);
    setChargingCommercial(undefined);
    setChargingIndustrial(undefined);
    setChargingPublic(undefined);
    setLocalPublicChargingRatio(undefined);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSave = async () => {
    if (!cityId) return;

    // Client-side validation
    const currentYear = new Date().getFullYear();
    const errors: string[] = [];
    
    if (targetYear !== undefined && targetYear - currentYear < 5) {
      errors.push(t('validationTargetYear', { year: currentYear }));
    }
    
    if (chargingResidential !== undefined && (chargingResidential < 0 || chargingResidential > 1)) {
      errors.push(t('validationChargingResidential'));
    }
    
    if (chargingCommercial !== undefined && (chargingCommercial < 0 || chargingCommercial > 1)) {
      errors.push(t('validationChargingCommercial'));
    }
    
    if (chargingIndustrial !== undefined && (chargingIndustrial < 0 || chargingIndustrial > 1)) {
      errors.push(t('validationChargingIndustrial'));
    }
    
    if (chargingPublic !== undefined && (chargingPublic < 0 || chargingPublic > 1)) {
      errors.push(t('validationChargingPublic'));
    }
    
    if (localPublicChargingRatio !== undefined && (localPublicChargingRatio < 0 || localPublicChargingRatio > 1)) {
      errors.push(t('validationLocalPublicRatio'));
    }
    
    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    // Build update data
    const updateData: any = {
      city_id: cityId,
      slot_number: currentSlot, // Add slot number
      neighborhood_id: undefined,
    };
    
    // Add only defined parameters - General
    if (targetYear !== undefined) updateData.target_year = targetYear;
    if (currentPopulation !== undefined) updateData.population_count = currentPopulation;
    if (futurePopulation !== undefined) updateData.population_count_future = futurePopulation;
    if (populationGrowthRate !== undefined) updateData.population_growth = populationGrowthRate;
    if (residentsCount !== undefined) updateData.residents_count = residentsCount;
    if (households !== undefined) updateData.households = households;
    
    // Areas - Current
    if (residentialArea !== undefined) updateData.residential_area = residentialArea;
    if (commercialArea !== undefined) updateData.commercial_area = commercialArea;
    if (industrialArea !== undefined) updateData.industrial_area = industrialArea;
    if (publicArea !== undefined) updateData.public_area = publicArea;
    if (culturalArea !== undefined) updateData.cultural_area = culturalArea;
    if (transportArea !== undefined) updateData.transport_area = transportArea;
    if (parkingInfraArea !== undefined) updateData.parking_infrastructure_area = parkingInfraArea;
    
    // Areas - Planned
    if (residentialAreaPlanned !== undefined) updateData.residential_area_planned = residentialAreaPlanned;
    if (commercialAreaPlanned !== undefined) updateData.commercial_area_planned = commercialAreaPlanned;
    if (industrialAreaPlanned !== undefined) updateData.industrial_area_planned = industrialAreaPlanned;
    if (publicAreaPlanned !== undefined) updateData.public_area_planned = publicAreaPlanned;
    if (culturalAreaPlanned !== undefined) updateData.cultural_area_planned = culturalAreaPlanned;
    if (transportAreaPlanned !== undefined) updateData.transport_area_planned = transportAreaPlanned;
    if (parkingInfraAreaPlanned !== undefined) updateData.parking_infrastructure_area_planned = parkingInfraAreaPlanned;
    
    // Vehicles
    if (vehiclesCount !== undefined) updateData.vehicles_count = vehiclesCount;
    if (vehiclesCountFuture !== undefined) updateData.vehicles_count_future = vehiclesCountFuture;
    if (carAvgAge !== undefined) updateData.car_avg_age = carAvgAge;
    if (residentialUnitsCount !== undefined) updateData.residential_units_count = residentialUnitsCount;
    if (residentialUnitsCountFuture !== undefined) updateData.residential_units_count_future = residentialUnitsCountFuture;
    
    // Energy - Consumption
    if (residentialEnergyConsumption !== undefined) updateData.residential_energy_consumption = residentialEnergyConsumption;
    if (commercialEnergyConsumption !== undefined) updateData.commercial_energy_consumption = commercialEnergyConsumption;
    if (industrialEnergyConsumption !== undefined) updateData.industrial_energy_consumption = industrialEnergyConsumption;
    if (publicEnergyConsumption !== undefined) updateData.public_energy_consumption = publicEnergyConsumption;
    
    // Energy - Renewable
    if (installedRenewableCapacity !== undefined) updateData.installed_renewable_energy_capacity = installedRenewableCapacity;
    if (renewablePotential !== undefined) updateData.urban_renewable_energy_potential = renewablePotential;
    if (plannedRenewableInfo !== undefined) updateData.planned_renewable_installations_info = plannedRenewableInfo;
    
    // Energy - Infrastructure
    if (substationCountCurrent !== undefined) updateData.substation_count_current = substationCountCurrent;
    if (substationCountFuture !== undefined) updateData.substation_count_future = substationCountFuture;
    if (futurePowerAdditionIec !== undefined) updateData.future_power_addition_iec = futurePowerAdditionIec;
    
    // Parking standards
    if (parkingResidential !== undefined) updateData.urban_parking_standard_residential = parkingResidential;
    if (parkingCommercial !== undefined) updateData.urban_parking_standard_commercial = parkingCommercial;
    if (parkingIndustrial !== undefined) updateData.urban_parking_standard_industrial = parkingIndustrial;
    if (parkingPublic !== undefined) updateData.urban_parking_standard_public = parkingPublic;
    
    // Charging standards
    if (chargingResidential !== undefined) updateData.residential_charging_standard = chargingResidential;
    if (chargingCommercial !== undefined) updateData.commercial_charging_standard = chargingCommercial;
    if (chargingIndustrial !== undefined) updateData.industrial_charging_standard = chargingIndustrial;
    if (chargingPublic !== undefined) updateData.public_charging_standard = chargingPublic;
    if (localPublicChargingRatio !== undefined) updateData.local_public_charging_users_ratio = localPublicChargingRatio;

    // Section 2.4.1: Impact preview and warnings ONLY for slot 1
    setLoading(true);
    try {
      // Only show impact warning for slot 1 (נתוני עיר)
      if (currentSlot === 1) {
        // Call preview endpoint for slots
        const preview = await previewUpdateSlotImpact(updateData);
        
        if (preview.has_errors) {
          const errorMessages = preview.validation_errors.map((e: any) => e.message).join("\n");
          alert(t('alertValidationErrors', { errors: errorMessages }));
          setLoading(false);
          return;
        }
        
        // Show warnings and get change reason
        let changeReason = "";
        let shouldProceed = true;
        
        if (preview.validation_warnings?.length > 0) {
          const warnings = preview.validation_warnings.map((w: any) => w.message).join("\n");
          const userResponse = prompt(
            t('promptSlotActiveWarning', { warnings, count: preview.affected_alternatives_count }),
            ""
          );
          
          if (!userResponse) {
            shouldProceed = false;
          } else {
            changeReason = userResponse;
            updateData.validation_warnings_overridden = preview.validation_warnings;
          }
        } else {
          // No warnings, still require change reason for slot 1
          changeReason = prompt(
            t('promptSlotChangeReason', { count: preview.affected_alternatives_count || 0 }),
            ""
          ) || "";
          
          if (!changeReason) {
            shouldProceed = false;
          }
        }
        
        if (!shouldProceed || !changeReason) {
          alert(t('alertUpdateCancelled'));
          setLoading(false);
          return;
        }
        
        // Add change reason to request
        updateData.change_reason = changeReason;
      } else {
        // For slots 2 and 3, just confirm (no impact on alternatives)
        const confirmed = confirm(
          t('confirmSlotUpdate', { slotName: SLOT_NAMES[currentSlot as keyof typeof SLOT_NAMES] })
        );
        
        if (!confirmed) {
          alert(t('alertUpdateCancelled'));
          setLoading(false);
          return;
        }
      }
      
      // Perform actual update using slot API
      const response = await updateSlot(updateData);
      
      // Check for warning message from slot 1 update
      if (response.warning_message) {
        alert(t('alertSuccessWithWarning', { warning: response.warning_message }));
      } else {
        alert(t('alertUpdateSuccess', { slotName: SLOT_NAMES[currentSlot as keyof typeof SLOT_NAMES] }));
      }
      
      // Store affected count for display
      if (response.data?.affected_alternatives_count) {
        setAffectedAlternativesCount(response.data.affected_alternatives_count);
      }
      
      // For slot 1, optionally trigger recalculation
      if (currentSlot === 1 && response.data?.affected_alternatives_count > 0) {
        const shouldRecalc = confirm(
          t('confirmRecalculate', { count: response.data.affected_alternatives_count })
        );
        
        if (shouldRecalc) {
          const recalcResponse = await fetch("/api/alternative/recalculate_all_by_city", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ city_id: cityId })
          });
          const recalcResult = await recalcResponse.json();
          
          if (recalcResult.status === "success") {
            alert(t('alertRecalculateSuccess', { count: recalcResult.data.processed }));
          }
        }
      }
      
    } catch (error: any) {
      console.error("Failed to update parameters:", error);
      alert(error.response?.data?.message || t('alertUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('pageTitle')}</h1>
        <h2 className={styles.subtitle}>{cityName}</h2>
      </div>

      {/* Section 2.4.1: Slot Selector */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        backgroundColor: '#f5f5f5',
        padding: '16px',
        marginBottom: '8px',
        borderRadius: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{t('selectParameterSet')}</h3>
          <Tabs 
            value={currentSlot - 1} 
            onChange={(e, newValue) => setCurrentSlot(newValue + 1)}
            aria-label="slot selector"
            sx={{ minHeight: '40px' }}
          >
            <Tab 
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {SLOT_NAMES[1]}
                  <span style={{ 
                    fontSize: '11px', 
                    backgroundColor: '#d32f2f', 
                    color: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    {t('affectsAlternatives')}
                  </span>
                </div>
              }
              sx={{ minHeight: '40px', textTransform: 'none' }}
            />
            <Tab 
              label={SLOT_NAMES[2]}
              sx={{ minHeight: '40px', textTransform: 'none' }}
            />
            <Tab 
              label={SLOT_NAMES[3]}
              sx={{ minHeight: '40px', textTransform: 'none' }}
            />
          </Tabs>
        </div>
        <div style={{ fontSize: '13px', color: '#666' }}>
          {currentSlot === 1 ? (
            <span style={{ color: '#d32f2f', fontWeight: 500 }}>
              {t('affectsAnalysis')}
            </span>
          ) : (
            <span style={{ color: '#388e3c' }}>
              {t('noAffectAnalysis')}
            </span>
          )}
        </div>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="city parameters tabs">
          <Tab label={t('tabGeneral')} />
          <Tab label={t('tabAreas')} />
          <Tab label={t('tabVehicles')} />
          <Tab label={t('tabEnergy')} />
          <Tab label={t('tabParkingStandards')} />
          <Tab label={t('tabChargingStandards')} />
        </Tabs>
      </Box>

      {/* Tab 0: General */}
      <TabPanel value={currentTab} index={0}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionGeneralParams')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelSourceYear')}</label>
            <InputField
              inputType="number"
              data={sourceYear}
              setData={() => {}} // Read-only
              id="source-year"
              placeholder="2025"
              disabled
            />
            <span className={styles.placeholder}>{sourceYear ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelTargetYear')}</label>
            <InputField
              inputType="number"
              data={targetYear || ""}
              setData={(value) => setTargetYear(value ? parseInt(value) : undefined)}
              id="target-year"
              placeholder="לדוגמה: 2040"
              tooltip={tooltips.targetYear}
            />
            <span className={styles.placeholder}>{targetYear ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCurrentPopulation')}</label>
            <InputField
              inputType="number"
              data={currentPopulation || ""}
              setData={(value) => setCurrentPopulation(value ? parseInt(value) : undefined)}
              id="current-population"
              placeholder="לדוגמה: 1069067"
              tooltip={tooltips.currentPopulation}
            />
            <span className={styles.placeholder}>{currentPopulation ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelFuturePopulation')}</label>
            <InputField
              inputType="number"
              data={futurePopulation || ""}
              setData={(value) => setFuturePopulation(value ? parseInt(value) : undefined)}
              id="future-population"
              placeholder="לדוגמה: 1500000"
              tooltip={tooltips.futurePopulation}
            />
            <span className={styles.placeholder}>{futurePopulation ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelPopulationGrowthRate')}</label>
            <InputField
              inputType="number"
              data={populationGrowthRate || ""}
              setData={(value) => setPopulationGrowthRate(value ? parseFloat(value) : undefined)}
              id="population-growth"
              placeholder="לדוגמה: 0.014"
              tooltip={tooltips.populationGrowthRate}
            />
            <span className={styles.placeholder}>{populationGrowthRate ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelResidentsCount')}</label>
            <InputField
              inputType="number"
              data={residentsCount || ""}
              setData={(value) => setResidentsCount(value ? parseInt(value) : undefined)}
              id="residents-count"
              placeholder="לדוגמה: 966210"
              tooltip={tooltips.residentsCount}
            />
            <span className={styles.placeholder}>{residentsCount ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelHouseholds')}</label>
            <InputField
              inputType="number"
              data={households || ""}
              setData={(value) => setHouseholds(value ? parseInt(value) : undefined)}
              id="households"
              placeholder="לדוגמה: 243150"
              tooltip={tooltips.households}
            />
            <span className={styles.placeholder}>{households ? "" : t('notSet')}</span>
          </div>
        </div>
      </TabPanel>

      {/* Tab 1: Areas */}
      <TabPanel value={currentTab} index={1}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionCurrentAreas')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelResidentialArea')}</label>
            <InputField
              inputType="number"
              data={residentialArea || ""}
              setData={(value) => setResidentialArea(value ? parseFloat(value) : undefined)}
              id="residential-area"
              placeholder="לדוגמה: 19591000"
              tooltip={tooltips.residentialArea}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCommercialArea')}</label>
            <InputField
              inputType="number"
              data={commercialArea || ""}
              setData={(value) => setCommercialArea(value ? parseFloat(value) : undefined)}
              id="commercial-area"
              placeholder="לדוגמה: 7652000"
              tooltip={tooltips.commercialArea}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelIndustrialArea')}</label>
            <InputField
              inputType="number"
              data={industrialArea || ""}
              setData={(value) => setIndustrialArea(value ? parseFloat(value) : undefined)}
              id="industrial-area"
              placeholder="לדוגמה: 1042000"
              tooltip={tooltips.industrialArea}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelPublicArea')}</label>
            <InputField
              inputType="number"
              data={publicArea || ""}
              setData={(value) => setPublicArea(value ? parseFloat(value) : undefined)}
              id="public-area"
              placeholder="לדוגמה: 3000000"
              tooltip={tooltips.publicArea}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCulturalArea')}</label>
            <InputField
              inputType="number"
              data={culturalArea || ""}
              setData={(value) => setCulturalArea(value ? parseFloat(value) : undefined)}
              id="cultural-area"
              placeholder="לדוגמה: 500000"
              tooltip={tooltips.culturalArea}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelTransportArea')}</label>
            <InputField
              inputType="number"
              data={transportArea || ""}
              setData={(value) => setTransportArea(value ? parseFloat(value) : undefined)}
              id="transport-area"
              placeholder="לדוגמה: 2000000"
              tooltip={tooltips.transportArea}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelParkingInfraArea')}</label>
            <InputField
              inputType="number"
              data={parkingInfraArea || ""}
              setData={(value) => setParkingInfraArea(value ? parseFloat(value) : undefined)}
              id="parking-infra-area"
              placeholder="לדוגמה: 6"
              tooltip={tooltips.parkingInfraArea}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionPlannedAreas')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelResidentialAddition')}</label>
            <InputField
              inputType="number"
              data={residentialAreaPlanned || ""}
              setData={(value) => setResidentialAreaPlanned(value ? parseFloat(value) : undefined)}
              id="residential-area-planned"
              placeholder="לדוגמה: 5000000"
              tooltip={tooltips.residentialAreaPlanned}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCommercialAddition')}</label>
            <InputField
              inputType="number"
              data={commercialAreaPlanned || ""}
              setData={(value) => setCommercialAreaPlanned(value ? parseFloat(value) : undefined)}
              id="commercial-area-planned"
              placeholder="לדוגמה: 1000000"
              tooltip={tooltips.commercialAreaPlanned}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelIndustrialAddition')}</label>
            <InputField
              inputType="number"
              data={industrialAreaPlanned || ""}
              setData={(value) => setIndustrialAreaPlanned(value ? parseFloat(value) : undefined)}
              id="industrial-area-planned"
              placeholder="לדוגמה: 500000"
              tooltip={tooltips.industrialAreaPlanned}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelPublicAddition')}</label>
            <InputField
              inputType="number"
              data={publicAreaPlanned || ""}
              setData={(value) => setPublicAreaPlanned(value ? parseFloat(value) : undefined)}
              id="public-area-planned"
              placeholder="לדוגמה: 1000000"
              tooltip={tooltips.publicAreaPlanned}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCulturalAddition')}</label>
            <InputField
              inputType="number"
              data={culturalAreaPlanned || ""}
              setData={(value) => setCulturalAreaPlanned(value ? parseFloat(value) : undefined)}
              id="cultural-area-planned"
              placeholder="לדוגמה: 200000"
              tooltip={tooltips.culturalAreaPlanned}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelTransportAddition')}</label>
            <InputField
              inputType="number"
              data={transportAreaPlanned || ""}
              setData={(value) => setTransportAreaPlanned(value ? parseFloat(value) : undefined)}
              id="transport-area-planned"
              placeholder="לדוגמה: 500000"
              tooltip={tooltips.transportAreaPlanned}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelParkingInfraAddition')}</label>
            <InputField
              inputType="number"
              data={parkingInfraAreaPlanned || ""}
              setData={(value) => setParkingInfraAreaPlanned(value ? parseFloat(value) : undefined)}
              id="parking-infra-area-planned"
              placeholder="לדוגמה: 2"
              tooltip={tooltips.parkingInfraAreaPlanned}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionHousingUnits')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCurrentHousingUnits')}</label>
            <InputField
              inputType="number"
              data={residentialUnitsCount || ""}
              setData={(value) => setResidentialUnitsCount(value ? parseInt(value) : undefined)}
              id="residential-units-count"
              placeholder="לדוגמה: 243150"
              tooltip={tooltips.residentialUnitsCount}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelFutureHousingUnits')}</label>
            <InputField
              inputType="number"
              data={residentialUnitsCountFuture || ""}
              setData={(value) => setResidentialUnitsCountFuture(value ? parseInt(value) : undefined)}
              id="residential-units-count-future"
              placeholder="לדוגמה: 367000"
              tooltip={tooltips.residentialUnitsCountFuture}
            />
          </div>
        </div>
      </TabPanel>

      {/* Tab 2: Vehicles */}
      <TabPanel value={currentTab} index={2}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionVehicles')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCurrentVehicles')}</label>
            <InputField
              inputType="number"
              data={vehiclesCount || ""}
              setData={(value) => setVehiclesCount(value ? parseInt(value) : undefined)}
              id="vehicles-count"
              placeholder="לדוגמה: 275070"
              tooltip={tooltips.vehiclesCount}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelFutureVehicles')}</label>
            <InputField
              inputType="number"
              data={vehiclesCountFuture || ""}
              setData={(value) => setVehiclesCountFuture(value ? parseInt(value) : undefined)}
              id="vehicles-count-future"
              placeholder="לדוגמה: 427034"
              tooltip={tooltips.vehiclesCountFuture}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelAvgVehicleAge')}</label>
            <InputField
              inputType="number"
              data={carAvgAge || ""}
              setData={(value) => setCarAvgAge(value ? parseFloat(value) : undefined)}
              id="car-avg-age"
              placeholder="לדוגמה: 9.1"
              tooltip={tooltips.carAvgAge}
            />
          </div>
        </div>
      </TabPanel>

      {/* Tab 3: Energy */}
      <TabPanel value={currentTab} index={3}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionEnergyConsumption')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelEnergyResidential')}</label>
            <InputField
              inputType="number"
              data={residentialEnergyConsumption || ""}
              setData={(value) => setResidentialEnergyConsumption(value ? parseFloat(value) : undefined)}
              id="residential-energy"
              placeholder="לדוגמה: 1628000000"
              tooltip={tooltips.residentialEnergyConsumption}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelEnergyCommercial')}</label>
            <InputField
              inputType="number"
              data={commercialEnergyConsumption || ""}
              setData={(value) => setCommercialEnergyConsumption(value ? parseFloat(value) : undefined)}
              id="commercial-energy"
              placeholder="לדוגמה: 1665000000"
              tooltip={tooltips.commercialEnergyConsumption}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelEnergyIndustrial')}</label>
            <InputField
              inputType="number"
              data={industrialEnergyConsumption || ""}
              setData={(value) => setIndustrialEnergyConsumption(value ? parseFloat(value) : undefined)}
              id="industrial-energy"
              placeholder="לדוגמה: 222000000"
              tooltip={tooltips.industrialEnergyConsumption}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelEnergyPublic')}</label>
            <InputField
              inputType="number"
              data={publicEnergyConsumption || ""}
              setData={(value) => setPublicEnergyConsumption(value ? parseFloat(value) : undefined)}
              id="public-energy"
              placeholder="לדוגמה: 148000000"
              tooltip={tooltips.publicEnergyConsumption}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionRenewableEnergy')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelInstalledCapacity')}</label>
            <InputField
              inputType="number"
              data={installedRenewableCapacity || ""}
              setData={(value) => setInstalledRenewableCapacity(value ? parseFloat(value) : undefined)}
              id="installed-renewable"
              placeholder="לדוגמה: 22.39"
              tooltip={tooltips.installedRenewableCapacity}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelRenewablePotential')}</label>
            <InputField
              inputType="number"
              data={renewablePotential || ""}
              setData={(value) => setRenewablePotential(value ? parseFloat(value) : undefined)}
              id="renewable-potential"
              placeholder="לדוגמה: 419.13"
              tooltip={tooltips.renewablePotential}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelPlannedInstallations')}</label>
            <InputField
              inputType="number"
              data={plannedRenewableInfo || ""}
              setData={(value) => setPlannedRenewableInfo(value ? parseFloat(value) : undefined)}
              id="planned-renewable"
              placeholder="לדוגמה: 5.0"
              tooltip={tooltips.plannedRenewableInfo}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionElectricInfrastructure')}</h3>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelCurrentSubstations')}</label>
            <InputField
              inputType="number"
              data={substationCountCurrent || ""}
              setData={(value) => setSubstationCountCurrent(value ? parseInt(value) : undefined)}
              id="substation-current"
              placeholder="לדוגמה: 11"
              tooltip={tooltips.substationCountCurrent}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelFutureSubstations')}</label>
            <InputField
              inputType="number"
              data={substationCountFuture || ""}
              setData={(value) => setSubstationCountFuture(value ? parseInt(value) : undefined)}
              id="substation-future"
              placeholder="לדוגמה: 20"
              tooltip={tooltips.substationCountFuture}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelFuturePowerAddition')}</label>
            <InputField
              inputType="number"
              data={futurePowerAdditionIec || ""}
              setData={(value) => setFuturePowerAdditionIec(value ? parseFloat(value) : undefined)}
              id="future-power-iec"
              placeholder="לדוגמה: 262.0"
              tooltip={tooltips.futurePowerAdditionIec}
            />
          </div>
        </div>
      </TabPanel>

      {/* Tab 4: Parking Standards */}
      <TabPanel value={currentTab} index={4}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionParkingStandards')}</h3>
          <p className={styles.description}>{t('descriptionParkingStandards')}</p>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelParkingResidential')}</label>
            <InputField
              inputType="number"
              data={parkingResidential || ""}
              setData={(value) => setParkingResidential(value ? parseFloat(value) : undefined)}
              id="parking-residential"
              placeholder="1.0"
              tooltip={tooltips.parkingResidential}
            />
            <span className={styles.placeholder}>{parkingResidential ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelParkingCommercial')}</label>
            <InputField
              inputType="number"
              data={parkingCommercial || ""}
              setData={(value) => setParkingCommercial(value ? parseFloat(value) : undefined)}
              id="parking-commercial"
              placeholder="0.01"
              tooltip={tooltips.parkingCommercial}
            />
            <span className={styles.placeholder}>{parkingCommercial ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelParkingIndustrial')}</label>
            <InputField
              inputType="number"
              data={parkingIndustrial || ""}
              setData={(value) => setParkingIndustrial(value ? parseFloat(value) : undefined)}
              id="parking-industrial"
              placeholder="0.01"
              tooltip={tooltips.parkingIndustrial}
            />
            <span className={styles.placeholder}>{parkingIndustrial ? "" : t('notSet')}</span>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelParkingPublic')}</label>
            <InputField
              inputType="number"
              data={parkingPublic || ""}
              setData={(value) => setParkingPublic(value ? parseFloat(value) : undefined)}
              id="parking-public"
              placeholder="0.01"
              tooltip={tooltips.parkingPublic}
            />
            <span className={styles.placeholder}>{parkingPublic ? "" : t('notSet')}</span>
          </div>
        </div>
      </TabPanel>

      {/* Tab 5: Charging Standards */}
      <TabPanel value={currentTab} index={5}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sectionChargingStandards')}</h3>
          <p className={styles.description}>{t('descriptionCharging')}</p>
          
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelChargingResidential')}</label>
            <InputField
              inputType="number"
              data={chargingResidential || ""}
              setData={(value) => setChargingResidential(value ? parseFloat(value) : undefined)}
              id="charging-residential"
              placeholder="0.2"
              tooltip={tooltips.chargingResidential}
            />
            <span className={styles.placeholder}>{chargingResidential ? "" : t('notSet')}</span>
            <p className={styles.hint}>{t('chargingHintPercent')}</p>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelChargingCommercialIndustrialPublic')}</label>
            <InputField
              inputType="number"
              data={chargingCommercial || ""}
              setData={(value) => setChargingCommercial(value ? parseFloat(value) : undefined)}
              id="charging-commercial"
              placeholder="1.0"
              tooltip={tooltips.chargingCommercial}
            />
            <span className={styles.placeholder}>{chargingCommercial ? "" : t('notSet')}</span>
            <p className={styles.hint}>{t('chargingHintFull')}</p>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelChargingIndustrial')}</label>
            <InputField
              inputType="number"
              data={chargingIndustrial || ""}
              setData={(value) => setChargingIndustrial(value ? parseFloat(value) : undefined)}
              id="charging-industrial"
              placeholder="0.5"
              tooltip={tooltips.chargingIndustrial}
            />
            <span className={styles.placeholder}>{chargingIndustrial ? "" : t('notSet')}</span>
            <p className={styles.hint}>{t('chargingHint')}</p>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelChargingPublic')}</label>
            <InputField
              inputType="number"
              data={chargingPublic || ""}
              setData={(value) => setChargingPublic(value ? parseFloat(value) : undefined)}
              id="charging-public"
              placeholder="0.3"
              tooltip={tooltips.chargingPublic}
            />
            <span className={styles.placeholder}>{chargingPublic ? "" : t('notSet')}</span>
            <p className={styles.hint}>{t('chargingHint')}</p>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('labelLocalPublicChargingRatio')}</label>
            <InputField
              inputType="number"
              data={localPublicChargingRatio || ""}
              setData={(value) => setLocalPublicChargingRatio(value ? parseFloat(value) : undefined)}
              id="local-public-charging"
              placeholder="0.5"
              tooltip={tooltips.localPublicChargingRatio}
            />
            <span className={styles.placeholder}>{localPublicChargingRatio ? "" : t('notSet')}</span>
            <p className={styles.hint}>{t('chargingHint')}</p>
          </div>
        </div>
      </TabPanel>

      <div className={styles.footer}>
        <Button
          id="save-parameters"
          text={t('saveButton')}
          onClick={handleSave}
          loading={loading}
          disabled={loading}
          icon="no"
        />
      </div>
    </div>
  );
};
